/* eslint-disable no-nested-ternary */
import { EDataType } from 'src/modules/network-configuration/models/node-data';

import { formatVlanIdsWithUt } from '../export-helpers';
import { buildExportHeaderRows } from './header-rows';
import { type NodeExportData, type ExportHeaderInfo } from './types';

const formatIpList = (ips: NodeExportData['IP']) =>
  ips
    .map((ip) =>
      ip.ip && ip.ip.trim() !== ''
        ? ip.subnetMask && ip.subnetMask.trim() !== ''
          ? `${ip.ip} (${ip.subnetMask})`
          : ip.ip
        : '',
    )
    .filter((ip) => ip !== '')
    .join('\n');

const formatEnabledPoe = (enabled?: boolean) => {
  if (enabled === true) return 'Yes';
  if (enabled === false) return 'No';
  return '';
};

function escapeCsvCell(value: string | number): string {
  const s = value == null ? '' : String(value);
  if (s.includes(',') || s.includes('"') || s.includes('\n'))
    return `"${s.replace(/"/g, '""')}"`;
  return s;
}

// Convert data to CSV format
export const exportToCSV = (
  data: any[],
  filename: string,
  headerInfo?: ExportHeaderInfo,
) => {
  if (data.length === 0) return;

  const headers = Object.keys(data[0]);
  const columnCount = headers.length;
  const headerRows = headerInfo
    ? buildExportHeaderRows(headerInfo, columnCount)
    : [];
  const headerCsv = headerRows
    .map((row) => row.map(escapeCsvCell).join(','))
    .join('\n');
  const dataCsv = [
    headers.join(','),
    ...data.map((row) =>
      headers.map((header) => escapeCsvCell(row[header])).join(','),
    ),
  ].join('\n');

  const csvContent = headerCsv ? `${headerCsv}\n${dataCsv}` : dataCsv;
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Helper: flatten node data for CSV (rows per port like Excel layout)
export const flattenSwitchesForCSV = (switchesData: NodeExportData[]) => {
  const rows: any[] = [];
  switchesData.forEach((s) => {
    const maxRows = Math.max(1, s.Port.length, s.IP.length);
    for (let i = 0; i < maxRows; i += 1) {
      const portObj = s.Port[i] || {};
      const ipObj = s.IP[i] || {};
      const vlanStr = portObj.vlanIds?.length
        ? formatVlanIdsWithUt(portObj.vlanIds, portObj.untaggedVlan)
        : '';
      const poeStr =
        portObj.isPoeCapable === false
          ? '-'
          : portObj.poeEnabled
            ? 'Yes'
            : 'No';
      rows.push({
        Name: i === 0 ? s.Name : '',
        Model: i === 0 ? s.Model : '',
        IP:
          ipObj.ip && ipObj.ip.trim() !== ''
            ? ipObj.subnetMask && ipObj.subnetMask.trim() !== ''
              ? `${ipObj.ip} (${ipObj.subnetMask})`
              : ipObj.ip
            : '',
        Port: portObj.name || '',
        'Connect Node': portObj.connectNode || '',
        'Connect Port': portObj.connectPort || '',
        'VLAN IDs': vlanStr,
        PoE: poeStr,
        DHCP: portObj.dhcpEnabled ? 'Yes' : 'No',
      });
    }
  });
  return rows;
};

export const flattenDevicesForCSV = (devicesData: NodeExportData[]) => {
  const rows: any[] = [];
  devicesData.forEach((d) => {
    if (d.Type === EDataType.Switch) {
      rows.push({
        Name: d.Name,
        Model: d.Model,
        IP: formatIpList(d.IP),
        'Enabled PoE': formatEnabledPoe(d.enabledPoe),
        'VLAN IDs': d.vlanIds?.sort((a, b) => a - b).join(', ') || '',
        'Connect Node': '',
        'Connect Port': '',
      });
      return;
    }

    const maxRows = Math.max(1, d.Port.length, d.IP.length);
    for (let i = 0; i < maxRows; i += 1) {
      const portObj = d.Port[i] || {};
      const ipObj = d.IP[i] || {};
      rows.push({
        Name: i === 0 ? d.Name : '',
        Model: i === 0 ? d.Model : '',
        IP:
          ipObj.ip && ipObj.ip.trim() !== ''
            ? ipObj.subnetMask && ipObj.subnetMask.trim() !== ''
              ? `${ipObj.ip} (${ipObj.subnetMask})`
              : ipObj.ip
            : '',
        'Enabled PoE': i === 0 ? formatEnabledPoe(d.enabledPoe) : '',
        'VLAN IDs': i === 0 ? d.vlanIds?.sort((a, b) => a - b).join(', ') : '',
        'Connect Node': portObj.connectNode || '',
        'Connect Port': portObj.connectPort || '',
      });
    }
  });
  return rows;
};
